import React, { useEffect, useState, useRef } from "react";
import {
  deleteOrder,
  fetchOrderReports,
  confirmOrder,
  dispatchOrder,
  deliverOrder,
  cancelOrder,
  markOrderPaid,
  pushToShipRocket,
  updateTrackingDetails,
} from "../../../services/orderService";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TablesRow from "../../Common/TableComponent/TablesRow";
import Swal from "sweetalert2";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { Loading } from "../../Common/OtherElements/Loading";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import StatusActionsMenu from "./StatusActionsMenu";
import TrackingActionsMenu from "./TrackingActionsMenu";

export const OrderReports = () => {
  const navigate = useNavigate();
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [manageOrders, setManageOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedTerm, setSearchedTerm] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [pageAccessDetails, setPageAccessDetails] = useState([]);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const PageLevelAccessurl = "order";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const [pushingToShipRocket, setPushingToShipRocket] = useState(null);
  const [updatingTracking, setUpdatingTracking] = useState(null);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    } else {
      console.log("No page access details found");
    }
  }, [pageAccessData, navigate]);

  const searchInputRef = useRef(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);

      // Format dates for the stored procedure (YYYY-MM-DD format)
      const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        // Use local date to avoid timezone issues
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const formData = {
        paginationQuery: {
          pageSize: entriesPerPage || 1,
          currentPage: currentPage || 1,
          filterParameter: searchedTerm || "",
        },
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
        orderStatus: orderStatus || "",
        paymentStatus: paymentStatus || "",
      };

      try {
        const response = await fetchOrderReports(formData);

        if (
          response.result &&
          Array.isArray(response.result.items) &&
          response.isSuccess
        ) {
          setManageOrders(response.result.items);
          setTotalCount(response.result?.totalCount || 0);
        } else {
          setManageOrders([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        handleErrors(error);
        setManageOrders([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [
    entriesPerPage,
    currentPage,
    fromDate,
    toDate,
    searchedTerm,
    orderStatus,
    paymentStatus,
  ]);

  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete("Order");
    if (confirmed) {
      try {
        await deleteOrder(id);
        setManageOrders((prev) => prev.filter((item) => item.id !== id));
        setTotalCount((prev) => prev - 1);
        Swal.fire(
          "Deleted!",
          "The Order has been deleted successfully.",
          "success"
        );
      } catch (error) {
        handleErrors(error);
      }
    }
  };
  // Handle push to ShipRocket
  const handlePushToShipRocket = async (orderGuid) => {
    const result = await Swal.fire({
      title: "Push to ShipRocket?",
      text: "Are you sure you want to push this order to ShipRocket for tracking?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Push to ShipRocket!",
    });

    if (result.isConfirmed) {
      setPushingToShipRocket(orderGuid);
      try {
        const response = await pushToShipRocket(orderGuid);

        // Check for successful response
        const isSuccess = response?.data?.isSuccess;

        if (isSuccess) {
          // Update the order with the new tracking code if provided in response
          const updatedOrder = response?.data?.result;
          setManageOrders((prev) =>
            prev.map((order) =>
              order.orderGuid === orderGuid
                ? {
                    ...order,
                    courierName: updatedOrder.courierName,
                    trackingCode: updatedOrder.trackingCode,
                    trackingLink: updatedOrder.trackingLink,
                  }
                : order
            )
          );

          const successMessage = "Order pushed to ShipRocket successfully!";

          Swal.fire("Success!", successMessage, "success");
        } else {
          throw new Error(
            response?.data?.statusMessage ||
              response?.statusMessage ||
              "Failed to push order to ShipRocket"
          );
        }
      } catch (error) {
        console.error("Error pushing to ShipRocket:", error);

        let errorMessage = "Failed to push order to ShipRocket";
        if (error.response?.data?.statusMessage) {
          errorMessage = error.response.data.statusMessage;
        } else if (error.response?.data?.statusMessage) {
          errorMessage = error.response.data.statusMessage;
        }

        Swal.fire("Error!", errorMessage, "error");
      } finally {
        setPushingToShipRocket(null);
      }
    }
  };
  // Handle order status updates
  const handleStatusUpdate = async (orderId, action, newStatus) => {
    const result = await Swal.fire({
      title: `${action} Order?`,
      text: `Are you sure you want to ${action.toLowerCase()} this order?`,
      icon: action.toLowerCase() === "cancel" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor:
        action.toLowerCase() === "cancel" ? "#d33" : "#3085d6",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${action}!`,
    });

    if (result.isConfirmed) {
      setUpdatingOrderId(orderId);
      try {
        let response;

        switch (action.toLowerCase()) {
          case "confirm":
            response = await confirmOrder(orderId);
            break;
          case "dispatch":
            response = await dispatchOrder(orderId);
            break;
          case "deliver":
            response = await deliverOrder(orderId);
            break;
          case "cancel":
            response = await cancelOrder(orderId);
            break;
          default:
            throw new Error("Invalid action");
        }

        // Check for successful response - handle different response structures
        const isSuccess =
          response?.data?.isSuccess ||
          response?.isSuccess ||
          response?.status === 200;

        if (isSuccess) {
          // Update the order in the local state instead of removing it
          setManageOrders((prev) =>
            prev.map((order) =>
              order.id === orderId
                ? { ...order, orderStatus: newStatus }
                : order
            )
          );

          const successMessage =
            response?.data?.statusMessage ||
            response?.statusMessage ||
            `Order ${action.toLowerCase()}ed successfully!`;

          Swal.fire("Success!", successMessage, "success");
        } else {
          throw new Error(
            response?.data?.statusMessage ||
              response?.statusMessage ||
              `Failed to ${action.toLowerCase()} order`
          );
        }
      } catch (error) {
        console.error(`Error ${action.toLowerCase()}ing order:`, error);

        let errorMessage = `Failed to ${action.toLowerCase()} order`;
        if (error.response?.data?.statusMessage) {
          errorMessage = error.response.data.statusMessage;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        Swal.fire("Error!", errorMessage, "error");
      } finally {
        setUpdatingOrderId(null);
      }
    }
  };

  const handlePaymentStatusUpdate = async (orderId) => {
    const result = await Swal.fire({
      title: "Mark Order as Paid?",
      text: "Are you sure you want to mark this COD order as paid?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Mark as Paid!",
    });

    if (result.isConfirmed) {
      setUpdatingOrderId(orderId);
      try {
        const response = await markOrderPaid(orderId);

        // Check for successful response
        const isSuccess =
          response?.data?.isSuccess ||
          response?.isSuccess ||
          response?.status === 200;

        if (isSuccess) {
          // Update the payment status in the local state
          setManageOrders((prev) =>
            prev.map((order) =>
              order.id === orderId
                ? {
                    ...order,
                    paymentStatus: "Paid",
                    paidAmount: order.totalPrice,
                    balanceAmount: 0,
                  }
                : order
            )
          );

          const successMessage =
            response?.data?.statusMessage ||
            response?.statusMessage ||
            "Order marked as paid successfully!";

          Swal.fire("Success!", successMessage, "success");
        } else {
          throw new Error(
            response?.data?.statusMessage ||
              response?.statusMessage ||
              "Failed to mark order as paid"
          );
        }
      } catch (error) {
        console.error("Error marking order as paid:", error);

        let errorMessage = "Failed to mark order as paid";
        if (error.response?.data?.statusMessage) {
          errorMessage = error.response.data.statusMessage;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        Swal.fire("Error!", errorMessage, "error");
      } finally {
        setUpdatingOrderId(null);
      }
    }
  };

  // Add this function to handle manual tracking updates
  const handleManualTrackingUpdate = async (orderGuid, trackingData) => {
    setUpdatingTracking(orderGuid);

    try {
      const payload = {
        orderGuid: orderGuid,
        courierName: trackingData.courierName,
        trackingCode: trackingData.trackingCode,
        trackingLink: trackingData.trackingLink || "",
      };

      const response = await updateTrackingDetails(payload);

      // Check for successful response
      const isSuccess =
        response?.data?.isSuccess ||
        response?.isSuccess ||
        response?.status === 200;

      if (isSuccess) {
        // Update the order with the new tracking details
        setManageOrders((prev) =>
          prev.map((order) =>
            order.orderGuid === orderGuid
              ? {
                  ...order,
                  courierName: trackingData.courierName,
                  trackingCode: trackingData.trackingCode,
                  trackingLink: trackingData.trackingLink || "",
                }
              : order
          )
        );
        const successMessage =
          response?.data?.statusMessage ||
          response?.statusMessage ||
          "Tracking details updated successfully!";

        Swal.fire("Success!", successMessage, "success");
      } else {
        throw new Error(
          response?.data?.statusMessage ||
            response?.statusMessage ||
            "Failed to update tracking details"
        );
      }
    } catch (error) {
      console.error("Error updating tracking details:", error);

      let errorMessage = "Failed to update tracking details";
      if (error.response?.data?.statusMessage) {
        errorMessage = error.response.data.statusMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire("Error!", errorMessage, "error");
    } finally {
      setUpdatingTracking(null);
    }
  };

  // Update the getAvailableActions function to include payment actions
  const getAvailableActions = (currentStatus, orderType, paymentStatus) => {
    const status = currentStatus?.toLowerCase();
    const actions = [];

    if (status === "in process") {
      actions.push({
        label: "Confirm Order",
        action: "Confirm",
        newStatus: "Confirmed",
        className: "dropdown-item text-primary",
        type: "status",
      });
      actions.push({
        label: "Cancel Order",
        action: "Cancel",
        newStatus: "Cancelled",
        className: "dropdown-item text-danger",
        type: "status",
      });
    } else if (status === "confirmed") {
      actions.push({
        label: "Dispatch Order",
        action: "Dispatch",
        newStatus: "Dispatched",
        className: "dropdown-item text-info",
        type: "status",
      });
      actions.push({
        label: "Cancel Order",
        action: "Cancel",
        newStatus: "Cancelled",
        className: "dropdown-item text-danger",
        type: "status",
      });
    } else if (status === "dispatched") {
      actions.push({
        label: "Mark as Delivered",
        action: "Deliver",
        newStatus: "Delivered",
        className: "dropdown-item text-success",
        type: "status",
      });
    } else if (status === "delivered") {
      // Add "Mark as Paid" option for COD orders that are not fully paid
      const isCodOrder = orderType?.toLowerCase().includes("cod");
      const isNotFullyPaid = paymentStatus?.toLowerCase() !== "paid";

      if (isCodOrder && isNotFullyPaid) {
        actions.push({
          label: "Mark as Paid",
          action: "MarkPaid",
          newStatus: "Paid",
          className: "dropdown-item text-success",
          type: "payment",
        });
      }
    }

    return actions;
  };

  const handleSearchClick = () => {
    setSearchedTerm(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchTerm("");
    setSearchedTerm("");
    setOrderStatus("");
    setPaymentStatus("");
    setCurrentPage(1);
  };

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <>
      <style>
        {`
          .table>:not(caption)>*>* {
            padding: .75rem 0.5rem !important;
          }
          .filter-row {
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
            align-items: end;
            margin-bottom: 20px;
          }
          .filter-group {
            display: flex;
            flex-direction: column;
            min-width: 150px;
          }
          .filter-group label {
            margin-bottom: 5px;
            font-weight: 500;
          }
          .btn-group-filters {
            display: flex;
            gap: 10px;
            align-items: end;
          }
          .status-actions-dropdown {
            position: relative;
          }
          .status-actions-dropdown .dropdown-menu {
            min-width: 160px;
          }
          .status-actions-dropdown .dropdown-item {
            padding: 8px 16px;
            font-size: 13px;
          }
          .status-actions-dropdown .dropdown-item:hover {
            background-color: #f8f9fa;
          }
          .action-spinner {
            width: 14px;
            height: 14px;
            border: 2px solid #f3f3f3;
            border-top: 2px solid #3498db;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            display: inline-block;
            margin-right: 8px;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage Orders</h5>
              </div>
              <div className="card-body">
                <div className=" row">
                  <div className="col-lg-3 mb-3">
                    <label htmlFor="search" className="form-label">
                      Search:
                    </label>
                    <input
                      type="text"
                      id="search"
                      className="form-control"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Order ID, Email, Phone, Name..."
                      ref={searchInputRef}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSearchClick();
                        }
                      }}
                    />
                  </div>
                  <div className="col-lg-3 mb-3">
                    <label htmlFor="fromDate" className="form-label">
                      From Date:
                    </label>
                    <Flatpickr
                      id="fromDate"
                      className="form-control"
                      placeholder="Select From Date"
                      value={fromDate}
                      onChange={([date]) => setFromDate(date)}
                      options={{
                        dateFormat: "Y-m-d",
                        monthSelectorType: "static",
                      }}
                    />
                  </div>

                  <div className="col-lg-3 mb-3">
                    <label htmlFor="toDate" className="form-label">
                      To Date:
                    </label>
                    <Flatpickr
                      id="toDate"
                      className="form-control"
                      placeholder="Select To Date"
                      value={toDate}
                      onChange={([date]) => setToDate(date)}
                      options={{
                        dateFormat: "Y-m-d",
                        monthSelectorType: "static",
                      }}
                    />
                  </div>

                  <div className="col-lg-3 mb-3">
                    <label htmlFor="orderStatus" className="form-label">
                      Order Status:
                    </label>
                    <select
                      className="form-select"
                      id="orderStatus"
                      value={orderStatus}
                      onChange={(e) => setOrderStatus(e.target.value)}
                    >
                      <option value="">All Statuses</option>
                      <option value="In Process">In Process</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Dispatched">Dispatched</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div className="col-lg-3 mb-3">
                    <label htmlFor="paymentStatus" className="form-label">
                      Payment Status:
                    </label>
                    <select
                      className="form-select"
                      id="paymentStatus"
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                    >
                      <option value="">All Payment Status</option>
                      <option value="Initiated">Initiated</option>
                      <option value="Partly Paid">Partly Paid</option>
                      <option value="Paid">Paid</option>
                    </select>
                  </div>
                  <div className="col-lg-6 mb-3 mt-4">
                    <div className="btn-group-filters">
                      <button
                        className="btn btn-primary"
                        onClick={handleSearchClick}
                      >
                        {" "}
                        Search
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={handleClearFilters}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
                <hr></hr>
                <div className="filter-row">
                  <div className="filter-group">
                    <label htmlFor="entriesPerPage" className="form-label">
                      Show entries:
                    </label>
                    <select
                      className="form-select"
                      id="entriesPerPage"
                      value={entriesPerPage}
                      onChange={handleEntriesPerPageChange}
                    >
                      <option value="30">30</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                    </select>
                  </div>
                </div>

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive table-nowrap">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "#",
                          "Order Details",
                          "Customer Info",
                          "Order Amount",
                          "Payment Status",
                          "Order Status",
                          "Invoice No",
                          "Shipping Actions",
                          "Ordered On",
                          "Status Actions",
                          "Actions",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {manageOrders.length === 0 ? (
                          <TableDataStatusError colspan="8" />
                        ) : (
                          manageOrders.map((order, index) => (
                            <TablesRow
                              key={order.orderGuid}
                              rowData={{
                                id:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                orderDetails: (
                                  <div>
                                    <div>
                                      <Link
                                        to={`${order.orderGuid}`}
                                        style={{
                                          textDecoration: "none",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {order.orderId}
                                      </Link>
                                    </div>
                                    <div
                                      className="text-muted"
                                      style={{ fontSize: "12px" }}
                                    >
                                      {order.orderType}
                                    </div>
                                  </div>
                                ),
                                customerInfo: (
                                  <div>
                                    <div
                                      className="mb-1"
                                      style={{ fontWeight: "600" }}
                                    >
                                      {order.userName}
                                    </div>
                                    <div
                                      className="mb-1"
                                      style={{ fontSize: "12px" }}
                                    >
                                      <i className="mdi mdi-email fs-12"></i>
                                      {order.emailId}
                                    </div>
                                    <div
                                      className="mb-1"
                                      style={{ fontSize: "12px" }}
                                    >
                                      <i className="mdi mdi-phone fs-12"></i>
                                      {order.mobileNo}
                                    </div>
                                  </div>
                                ),
                                orderAmount: (
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        color: "#28a745",
                                      }}
                                    >
                                      {formatCurrency(order.totalPrice)}
                                    </div>
                                    {order.discount > 0 && (
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: "10px" }}
                                      >
                                        Discount:{" "}
                                        {formatCurrency(order.discount)}
                                      </div>
                                    )}
                                    <div
                                      className="text-muted"
                                      style={{ fontSize: "10px" }}
                                    >
                                      Paid: {formatCurrency(order.paidAmount)}
                                    </div>
                                    {order.balanceAmount > 0 && (
                                      <div
                                        className="text-danger"
                                        style={{ fontSize: "10px" }}
                                      >
                                        Balance:{" "}
                                        {formatCurrency(order.balanceAmount)}
                                      </div>
                                    )}
                                  </div>
                                ),
                                paymentStatus: (
                                  <span
                                    className={`badge ${
                                      order.paymentStatus === "Paid"
                                        ? "badge-soft-success"
                                        : order.paymentStatus === "Pending"
                                        ? "badge-soft-warning"
                                        : order.paymentStatus === "Partly Paid"
                                        ? "badge-soft-warning"
                                        : order.paymentStatus === "Failed"
                                        ? "badge-soft-danger"
                                        : "badge-soft-secondary"
                                    } badge-border`}
                                    style={{ fontSize: "11px" }}
                                  >
                                    {order.paymentStatus || "N/A"}
                                  </span>
                                ),
                                orderStatus: (
                                  <span
                                    className={`badge ${
                                      order.orderStatus === "Delivered"
                                        ? "badge-soft-success"
                                        : order.orderStatus === "Dispatched"
                                        ? "badge-soft-info"
                                        : order.orderStatus === "In Process"
                                        ? "badge-soft-warning"
                                        : order.orderStatus === "Cancelled"
                                        ? "badge-soft-danger"
                                        : "badge-soft-secondary"
                                    } badge-border`}
                                    style={{ fontSize: "11px" }}
                                  >
                                    {order.orderStatus}
                                  </span>
                                ),
                                invoiceNo: (
                                  <div
                                    className="badge badge-soft-primary badge-border "
                                    style={{ fontSize: "12px" }}
                                  >
                                    {order.invoiceNo && (
                                      <Link
                                        to={`invoice/${order.orderGuid}`}
                                        style={{
                                          textDecoration: "none",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {order.invoiceNo}
                                      </Link>
                                    )}
                                  </div>
                                ),
                                tracking: (
                                  <TrackingActionsMenu
                                    order={order}
                                    pushingToShipRocket={pushingToShipRocket}
                                    updatingTracking={updatingTracking}
                                    handlePushToShipRocket={
                                      handlePushToShipRocket
                                    }
                                    handleManualTrackingUpdate={
                                      handleManualTrackingUpdate
                                    }
                                  />
                                ),
                                orderedOn: formatDate(order.orderedOn),
                                statusActions: (
                                  <StatusActionsMenu
                                    order={order}
                                    updatingOrderId={updatingOrderId}
                                    handleStatusUpdate={handleStatusUpdate}
                                    handlePaymentStatusUpdate={
                                      handlePaymentStatusUpdate
                                    }
                                    getAvailableActions={getAvailableActions}
                                  />
                                ),
                              }}
                              columns={[
                                "id",
                                "orderDetails",
                                "customerInfo",
                                "orderAmount",
                                "paymentStatus",
                                "orderStatus",
                                "invoiceNo",
                                "tracking",
                                "orderedOn",
                                "statusActions",
                              ]}
                              hideIcons={false}
                              onDelete={() => handleDelete(order.id)}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {!loading && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalEntries={totalCount}
                    entriesPerPage={entriesPerPage}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
export default OrderReports;
